import { useEffect, useRef, useState } from 'react';

// Image optimization utility
export interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  placeholder?: string;
  onLoad?: () => void;
  onError?: () => void;
}

// Hook for lazy loading images
export const useLazyImage = (src: string, options: { threshold?: number } = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: options.threshold || 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [options.threshold]);

  useEffect(() => {
    if (isInView && src) {
      const img = new Image();
      img.onload = () => setIsLoaded(true);
      img.onerror = () => setError('Failed to load image');
      img.src = src;
    }
  }, [isInView, src]);

  return { isLoaded, isInView, error, imgRef };
};

// Generate responsive image sources
export const generateResponsiveImageSources = (
  baseSrc: string,
  sizes: number[] = [320, 640, 768, 1024, 1280]
) => {
  const extension = baseSrc.split('.').pop();
  const baseName = baseSrc.replace(`.${extension}`, '');

  return sizes.map(size => ({
    srcSet: `${baseName}-${size}w.${extension} ${size}w`,
    media: `(max-width: ${size}px)`,
  }));
};

// WebP support detection
export const supportsWebP = (): Promise<boolean> => {
  return new Promise(resolve => {
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2);
    };
    webP.src =
      'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  });
};

// Image format optimization
export const getOptimizedImageSrc = async (src: string): Promise<string> => {
  const isWebPSupported = await supportsWebP();

  if (isWebPSupported && !src.includes('.webp')) {
    // Try to get WebP version
    const webpSrc = src.replace(/\.(jpg|jpeg|png)$/i, '.webp');

    // Check if WebP version exists
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(webpSrc);
      img.onerror = () => resolve(src);
      img.src = webpSrc;
    });
  }

  return src;
};

// Preload critical images
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

// Preload multiple images
export const preloadImages = async (sources: string[]): Promise<void> => {
  try {
    await Promise.all(sources.map(src => preloadImage(src)));
  } catch (error) {
    console.warn('Some images failed to preload:', error);
  }
};

// Image compression utility (for client-side compression)
export const compressImage = (
  file: File,
  quality: number = 0.8,
  maxWidth: number = 1920
): Promise<Blob> => {
  return new Promise(resolve => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();

    img.onload = () => {
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        blob => {
          resolve(blob!);
        },
        'image/jpeg',
        quality
      );
    };

    img.src = URL.createObjectURL(file);
  });
};

// Critical images that should be preloaded
export const CRITICAL_IMAGES = [
  '/images/hero-bg.jpg',
  '/images/logo.svg',
  '/images/services-hero.jpg',
];

// Initialize image optimizations
export const initImageOptimizations = async () => {
  // Preload critical images
  await preloadImages(CRITICAL_IMAGES);

  // Set up intersection observer for lazy loading
  if ('IntersectionObserver' in window) {
    const lazyImages = document.querySelectorAll('img[data-lazy]');
    const imageObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.dataset.lazy;
          if (src) {
            img.src = src;
            img.removeAttribute('data-lazy');
            imageObserver.unobserve(img);
          }
        }
      });
    });

    lazyImages.forEach(img => imageObserver.observe(img));
  }
};
